import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { PrimaryButton } from './PrimaryButton';
import { FloatingLabelInput } from './FloatingLabelInput';
import {
  getMe,
  login,
  proTwofaSetup,
  proTwofaVerify,
} from '../../lib/professional-api';
import { patchUser, setAuth, type AuthUser } from '../../lib/auth';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

// 'credentials' = email + password; 'setup' = first-login QR enrolment;
// 'verify' = enter the 6-digit authenticator code.
type Step = 'credentials' | 'setup' | 'verify';

export function ProSignInForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({ email: false, password: false });
  const [submitting, setSubmitting] = useState(false);

  // Two-factor state.
  const [step, setStep] = useState<Step>('credentials');
  const [challengeToken, setChallengeToken] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  // Land the session + route the pro once a real token comes back (from the
  // 2FA verify step, or a legacy no-2FA response).
  const finishLogin = async (res: { accessToken: string; user: AuthUser }) => {
    setAuth(res.accessToken, res.user);
    toast.success('Welcome back!');

    // Refresh authoritative state from /professional/me before deciding
    // where to send them (cached `status` can be stale, e.g. admin just
    // approved them between sessions).
    let status = res.user.status;
    let emailVerifiedAt = res.user.email_verified_at;
    // Onboarding position (0 = at verify-identity, 1..10 = in the wizard) and
    // whether the wizard is fully done. Drives resume so a returning pro isn't
    // dumped back at the start.
    let onboardingStep = 0;
    let onboardingDone = false;
    try {
      const me = await getMe();
      const info: any = me?.info ?? {};
      const u = info.user ?? info;
      if (u?.status) status = u.status;
      if (u?.email_verified_at !== undefined) {
        emailVerifiedAt = u.email_verified_at;
      }
      onboardingStep = Number(info?.onboarding_step) || 0;
      onboardingDone = !!info?.onboarding_completed_at;
      patchUser({
        status,
        email_verified_at: emailVerifiedAt ?? null,
        onboarding_step: onboardingStep,
      } as any);
    } catch {
      // Server unreachable / stale token — fall back to the login response.
    }

    if (!emailVerifiedAt) {
      navigate('/professional/signup/success', {
        state: { email: res.user.email },
      });
    } else if (status === 'APPROVED') {
      navigate('/professional/dashboard');
    } else if (!onboardingDone && onboardingStep >= 1) {
      // Mid-wizard — RESUME where they left off instead of restarting at the
      // identity page. The wizard reads onboarding_step and opens on that step.
      navigate('/professional/service-offer');
    } else {
      // Step 0 (haven't cleared identity yet) or already finished (awaiting
      // review) → the identity/documents page.
      navigate('/professional/verify-identity');
    }
  };

  const handleSignIn = async () => {
    const newErrors = {
      email: !email || !validateEmail(email),
      password: !password,
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    setSubmitting(true);
    try {
      const res = await login({
        email: email.trim().toLowerCase(),
        password,
      });

      // Legacy / 2FA-disabled fallback: a full token came straight back.
      if ('accessToken' in res && res.accessToken) {
        await finishLogin(res);
        return;
      }

      if (!('challenge_token' in res) || !res.challenge_token) {
        throw new Error('Could not sign in.');
      }
      setChallengeToken(res.challenge_token);

      if (res.requires_2fa_setup) {
        const setup = await proTwofaSetup(res.challenge_token);
        setQrDataUrl(setup.qr_data_url ?? '');
        setSecret(setup.secret ?? '');
        setStep('setup');
      } else {
        setStep('verify');
      }
    } catch (err) {
      // Default to the specific, friendly credential message — apiErrorMessage
      // still prefers a real server message when present, and surfaces genuine
      // network errors ("Network Error") rather than this fallback. So this
      // only shows when the server gave nothing usable, which in the login
      // context is an invalid-credentials case.
      toast.error(apiErrorMessage(err, 'Invalid email or password.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    const trimmed = code.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(trimmed)) {
      setCodeError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setSubmitting(true);
    setCodeError('');
    try {
      const res = await proTwofaVerify(challengeToken, trimmed);
      await finishLogin(res);
    } catch (err) {
      setCodeError(
        apiErrorMessage(err, 'That code could not be verified.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (step !== 'credentials') {
    return (
      <div className="w-full flex flex-col items-center max-w-[420px] mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2">
            Two-step verification
          </h1>
          <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
            {step === 'setup'
              ? 'Scan the QR code with an authenticator app (Google Authenticator, Authy…), then enter the 6-digit code to finish setup.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>
        </div>

        <div className="w-full flex flex-col gap-6">
          {step === 'setup' && qrDataUrl ? (
            <div className="flex flex-col items-center">
              <img
                src={qrDataUrl}
                alt="2FA QR code"
                className="h-44 w-44 rounded-lg border border-[#eee]"
              />
              {secret ? (
                <p className="mt-3 text-center text-[12px] text-[#908e8e]">
                  Can't scan? Enter this key manually:
                  <br />
                  <span className="font-mono text-[13px] tracking-wider text-[#3a3335] break-all">
                    {secret}
                  </span>
                </p>
              ) : null}
            </div>
          ) : null}

          <div>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              autoFocus
              onChange={(e) => {
                setCode(e.target.value.replace(/[^\d]/g, ''));
                if (codeError) setCodeError('');
              }}
              className="w-full rounded-[10px] border border-[#eee] px-4 py-4 text-center text-[24px] tracking-[0.5em] text-[#3a3335] focus:border-[#8e2a2f] focus:outline-none"
            />
            {codeError ? (
              <p className="mt-2 text-[13px] text-[#dc2626]">{codeError}</p>
            ) : null}
          </div>

          <PrimaryButton
            onClick={handleVerify}
            className="w-full py-4 mt-2"
            disabled={submitting}
          >
            {submitting
              ? 'Verifying…'
              : step === 'setup'
                ? 'Verify & finish setup'
                : 'Verify'}
          </PrimaryButton>

          <button
            type="button"
            onClick={() => {
              setStep('credentials');
              setCode('');
              setCodeError('');
              setQrDataUrl('');
              setSecret('');
              setChallengeToken('');
            }}
            className="text-center font-['DM_Sans',sans-serif] text-[13px] font-bold text-[#908e8e] hover:text-[#3a3335]"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center max-w-[420px] mx-auto">
      <div className="text-center mb-12">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2">
          Sign In as a Pro
        </h1>
        <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
          Enter your professional details to proceed
        </p>
      </div>

      <div className="w-full flex flex-col gap-6">
        <FloatingLabelInput
          label="Email address"
          type="email"
          value={email}
          onChange={(val) => {
            setEmail(val);
            setErrors({ ...errors, email: false });
          }}
          icon={``}
          className="w-full"
          error={errors.email}
          errorMessage="Please enter a valid email address"
        />

        <FloatingLabelInput
          label="Password"
          type="password"
          value={password}
          onChange={(val) => {
            setPassword(val);
            setErrors({ ...errors, password: false });
          }}
          icon={``}
          className="w-full"
          error={errors.password}
          errorMessage="Password is required"
        />

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setRememberMe(!rememberMe)}
            className="flex items-center gap-[10px] cursor-pointer group"
          >
            <div
              className={`size-[20px] rounded-full flex items-center justify-center transition-colors border ${rememberMe ? 'bg-[#8e2a2f] border-[#8e2a2f]' : 'bg-white border-[#eee]'}`}
            >
              {rememberMe && <span className="text-white text-[12px]">✓</span>}
            </div>
            <span className="font-['DM_Sans',sans-serif] font-bold leading-[21px] text-[#3a3335] text-[14px] tracking-[-0.1px]">
              Remember me
            </span>
          </button>

          <Link
            to="/professional/forgot-password"
            className="font-['DM_Sans',sans-serif] font-bold leading-[21px] text-[#8e2a2f] text-[14px] tracking-[-0.1px] hover:underline"
          >
            Recover password
          </Link>
        </div>

        <PrimaryButton
          onClick={handleSignIn}
          className="w-full py-4 mt-2"
          disabled={submitting}
        >
          {submitting ? 'Signing in…' : 'Sign In'}
        </PrimaryButton>

        <div className="text-center mt-4">
          <p className="font-['DM_Sans',sans-serif] text-[14px] text-[#908e8e]">
            Don't have a professional account?{' '}
            <Link to="/professional/signup" className="text-[#8e2a2f] font-bold hover:underline">
              Join as a Pro
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
